import { Alert, Button, IconButton, TextField, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { CatalogService } from "@services/catalog/catalogService";
import { DimensionValue } from "@models/catalog.model";
import "../catalog.scss";

// The curated value list for one axis (MPN-1278). Each row is a canonical code plus the messy
// encodings that fold onto it — fall26 / 26FA / 202608 all become 2026FA at load time.
//
// Aliases were the reason this screen had to exist: the loader logs "unrecognised dimension
// values (add as aliases)" on every unmatched value, and until now that instruction pointed at
// Django admin.

type Draft = { code: string; name: string; aliases: string; sequence: number };

const toDraft = (v: DimensionValue): Draft => ({
  code: v.code, name: v.name, aliases: (v.aliases ?? []).join(", "), sequence: v.sequence,
});

const toPayload = (d: Draft) => ({
  code: d.code.trim(),
  name: d.name.trim(),
  aliases: d.aliases.split(",").map((a) => a.trim()).filter(Boolean),
  sequence: d.sequence,
});

export function DimensionValuesEditor({
  dimensionId, onChanged,
}: {
  dimensionId: number;
  /** Each row is written immediately, so the caller has to know a change landed even if the
      dialog is then dismissed with Cancel — otherwise the axis's value list reads stale. */
  onChanged?: () => void;
}) {
  const svc = new CatalogService();
  const [values, setValues] = useState<DimensionValue[]>([]);
  const [editing, setEditing] = useState<number | "new" | null>(null);
  const [draft, setDraft] = useState<Draft>({ code: "", name: "", aliases: "", sequence: 0 });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => svc.listDimensionValues(dimensionId).then(setValues).catch(() => setValues([]));
  useEffect(() => { load(); }, [dimensionId]);

  const startNew = () => {
    setError(null);
    setDraft({ code: "", name: "", aliases: "", sequence: values.length });
    setEditing("new");
  };

  const startEdit = (v: DimensionValue) => {
    setError(null);
    setDraft(toDraft(v));
    setEditing(v.id);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (editing === "new") await svc.createDimensionValue(dimensionId, toPayload(draft));
      else if (editing !== null) await svc.updateDimensionValue(dimensionId, editing, toPayload(draft));
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e) {
      // The API rejects an alias that collides with its own code or another value's, and names
      // the owner. Surfacing that verbatim is more use than "save failed".
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (v: DimensionValue) => {
    setError(null);
    try {
      await svc.deleteDimensionValue(dimensionId, v.id);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
    }
  };

  return (
    <div className="dimension-values">
      <div className="dimension-values__head">
        <span className="dimension-values__title">Values</span>
        <span className="dimension-values__hint">
          The canonical list this axis folds incoming data onto
        </span>
      </div>

      {values.length === 0 && editing === null && (
        // The behaviour change worth saying out loud: an axis with no values passes everything
        // through untouched. The first value makes it managed, and anything not listed is then
        // flagged as uncurated on every load.
        <Alert severity="info" className="dimension-values__notice">
          This axis isn’t curated yet, so every value in the data passes through as-is. Adding the
          first value switches it to a managed list — from then on anything not listed here is
          flagged for curation when a file loads.
        </Alert>
      )}

      {error && <Alert severity="error" className="dimension-values__notice">{error}</Alert>}

      <table className="dimension-values__table">
        <thead>
          <tr>
            <th>Code</th><th>Name</th><th>Also accepted as</th><th>Order</th><th />
          </tr>
        </thead>
        <tbody>
          {values.map((v) => (
            editing === v.id ? (
              <DraftRow key={v.id} draft={draft} setDraft={setDraft} onSave={save}
                onCancel={() => setEditing(null)} busy={busy} codeLocked />
            ) : (
              <tr key={v.id}>
                <td><code>{v.code}</code></td>
                <td>{v.name || <span className="muted">—</span>}</td>
                <td>
                  {v.aliases?.length
                    ? v.aliases.map((a) => <code key={a} className="alias">{a}</code>)
                    : <span className="muted">—</span>}
                </td>
                <td>{v.sequence}</td>
                <td className="dimension-values__actions">
                  <Button size="small" onClick={() => startEdit(v)}>Edit</Button>
                  <Tooltip title="Remove from the curated list">
                    <IconButton size="small" onClick={() => remove(v)} aria-label={`Delete ${v.code}`}>
                      <i className="bi bi-trash" />
                    </IconButton>
                  </Tooltip>
                </td>
              </tr>
            )
          ))}
          {editing === "new" && (
            <DraftRow draft={draft} setDraft={setDraft} onSave={save}
              onCancel={() => setEditing(null)} busy={busy} />
          )}
        </tbody>
      </table>

      {editing === null && (
        <Button size="small" onClick={startNew} startIcon={<i className="bi bi-plus" />}>
          Add value
        </Button>
      )}
    </div>
  );
}

function DraftRow({
  draft, setDraft, onSave, onCancel, busy, codeLocked = false,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  onSave: () => void;
  onCancel: () => void;
  busy: boolean;
  // Editing an existing value: the code is its identity — stamped on every stored row and
  // named by every field map and rule — so it never changes. The Name is the label (MPN-1663).
  codeLocked?: boolean;
}) {
  return (
    <tr className="dimension-values__draft">
      <td>
        {codeLocked ? (
          <code title="The code is the stored value and cannot change. Edit the Name to change how it is shown.">
            {draft.code}
          </code>
        ) : (
          <TextField size="small" value={draft.code} autoFocus placeholder="2026FA"
            onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
        )}
      </td>
      <td>
        <TextField size="small" value={draft.name} placeholder="Fall 2026" autoFocus={codeLocked}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
      </td>
      <td>
        {/* The placeholder is the whole instruction — it shows the comma format and what a real
            customer encoding looks like, so a helper line under it only made the row taller. */}
        <TextField size="small" fullWidth value={draft.aliases} placeholder="fall26, 26FA, 202608"
          onChange={(e) => setDraft({ ...draft, aliases: e.target.value })} />
      </td>
      <td>
        <TextField size="small" type="number" value={draft.sequence}
          onChange={(e) => setDraft({ ...draft, sequence: Number(e.target.value) })} />
      </td>
      <td className="dimension-values__actions">
        <Button size="small" variant="contained" disabled={!draft.code.trim() || busy}
          onClick={onSave}>Save</Button>
        <Button size="small" onClick={onCancel}>Cancel</Button>
      </td>
    </tr>
  );
}
